import { useLingui } from '@lingui/react/macro'
import { Separator } from 'designSystem/components/Separator'
import { useOpenTrendResource } from 'features/facilityModel/useFacilityModelNavigation'
import { useDocumentTabDataContext } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import type { SelectedGraphNode } from 'features/knowledgeDocumentViewer/graph/GraphNodeSelectionContext'
import { GraphSummaryDetails } from 'features/knowledgeDocumentViewer/graph/GraphSummaryDetails'
import { buildNodeTooltip } from 'features/knowledgeDocumentViewer/graph/nodeTooltip'
import { useNodePageDestinations } from 'features/knowledgeDocumentViewer/graph/useNodePageDestinations'
import { useDocumentSubjectsPreload } from 'features/knowledgeDocumentViewer/hooks/useDocumentSubjects'
import { useSystemDocumentsByBqIds } from 'features/knowledgeDocumentViewer/hooks/useSystemDocumentsByBqIds'
import { type FC, useCallback, useMemo } from 'react'

import { EntityPanel } from './EntityPanel'
import { type NodeDocumentRow, nodeDocumentRows } from './relatedDocumentRows'
import {
  RelatedDocumentsMessage,
  RelatedDocumentsTable,
} from './RelatedDocumentsTable'
import { TimeSeriesSection } from './TimeSeriesSection'

interface GraphNodePanelProps {
  selection: SelectedGraphNode
  scrollWithParent?: boolean
  onClose: () => void
}

export const GraphNodePanel: FC<GraphNodePanelProps> = ({
  selection: { node, datapoints },
  onClose,
  scrollWithParent = false,
}) => {
  const { t } = useLingui()
  const { siteID, documentId } = useDocumentTabDataContext()
  const { addDocumentTab } = useKnowledgeDocumentUXContext()
  const openTrend = useOpenTrendResource(siteID, 'document-viewer')
  const preloadDocumentSubjects = useDocumentSubjectsPreload()
  const details = buildNodeTooltip(node)

  const pages = useNodePageDestinations(node)
  const pageDocumentIds = useMemo(
    () => (pages.destinations ?? []).map((page) => page.documentId),
    [pages.destinations]
  )
  const systemDocuments = useSystemDocumentsByBqIds(pageDocumentIds)
  const docRows = useMemo(
    () =>
      nodeDocumentRows(
        pages.destinations ?? [],
        systemDocuments.documents ?? [],
        documentId
      ),
    [pages.destinations, systemDocuments.documents, documentId]
  )

  const preloadDocument = useCallback(
    (row: NodeDocumentRow) => {
      void preloadDocumentSubjects(siteID, row.openId)
    },
    [preloadDocumentSubjects, siteID]
  )

  const openDocument = useCallback(
    (row: NodeDocumentRow) => {
      addDocumentTab({
        documentId: row.openId,
        pageNumber: row.pageNumber,
        selectedGraphNodeId: node.id,
      })
    },
    [addDocumentTab, node.id]
  )

  return (
    <EntityPanel
      scrollWithParent={scrollWithParent}
      eyebrow={details.subtitle}
      title={details.title}
      actions={null}
      closeLabel={t`Close node panel`}
      onClose={onClose}>
      {details.rows.length > 0 && <GraphSummaryDetails rows={details.rows} />}
      {details.rows.length > 0 && datapoints.length > 0 && <Separator />}
      {datapoints.length > 0 && (
        <TimeSeriesSection datapoints={datapoints} onOpenTrend={openTrend} />
      )}
      {pages.error ? (
        <RelatedDocumentsMessage
          message={t`Could not load documents for this node.`}
        />
      ) : pages.isLoading || systemDocuments.isLoading ? (
        <RelatedDocumentsMessage message={t`Loading documents…`} />
      ) : docRows.length === 0 ? (
        <RelatedDocumentsMessage message={t`No documents show this node.`} />
      ) : (
        <RelatedDocumentsTable
          rows={docRows}
          openingDocId={null}
          onPreload={preloadDocument}
          onOpen={openDocument}
        />
      )}
    </EntityPanel>
  )
}
